import React from 'react';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { BondingCurve } from '@components/Song/MintComponents/MintComponent/BondingCurve';
import { type MintReferral, useMintReferral } from '@context/mintReferral';
import { type FragmentType, MintComponentFragmentDoc } from '@graphql/reactQuery';
import { getFragment, gql } from '@graphql/reactQuery';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { EditionMintComponent } from './Edition';
import { MintComponentProvider } from './Edition/Context';
import { TiersMintComponent } from './Tiers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment MintComponent on Release {
    id
    samAddress
    auctionContractType
    contractAddress
    finalQuantity
    chainId
    ...RegularMintInfo
    ...BondingCurveMintData
    ...ReleaseUpdates
    ...MintDataContracts
    ...TiersMintComponent
  }
`);

export const MintComponent = ({
  releaseData,
  collectButtonSource,
  isInModal = false,
  referral,
}: {
  releaseData: FragmentType<MintComponentFragmentDoc>;
  collectButtonSource: CollectButtonSource;
  isInModal?: boolean;
  referral: MintReferral;
}) => {
  const release = getFragment(MintComponentFragmentDoc, releaseData);
  const { setReferral } = useMintReferral();

  useDeepCompareEffectNoCheck(() => {
    setReferral(referral);
  }, [referral, setReferral]);

  const {
    data: { isFirstPhaseCompleted },
  } = useSubscribeRelease({
    release,
  });

  const isTieredRelease = release.auctionContractType === 'TIERED_EDITION';
  const isSamEnabled = !!release.samAddress;
  const shouldShowBondingCurve =
    release.auctionContractType === 'EDITION' && isFirstPhaseCompleted && isSamEnabled;

  if (shouldShowBondingCurve) {
    return (
      <div className="mt-3 lt:mt-8">
        <BondingCurve
          data={release}
          contractAddress={release.contractAddress}
          collectButtonSource={collectButtonSource}
          isInModal={isInModal}
        />
      </div>
    );
  }

  // Prevent showing mint component when it's missing critical auction information
  if (!release.auctionContractType) return null;

  if (isTieredRelease) {
    return (
      <div className="mx-auto mt-3 flex w-full lt:mt-8 lt:max-w-sm">
        <TiersMintComponent
          contractAddress={release.contractAddress}
          chainId={release.chainId}
          release={release}
          collectButtonSource={collectButtonSource}
          isInModal={isInModal}
        />
      </div>
    );
  }

  return (
    <MintComponentProvider
      initialState={{
        release: releaseData,
      }}
    >
      <div className="mx-auto mt-3 flex w-full lt:mt-8 lt:max-w-sm">
        <EditionMintComponent
          releaseInfo={release}
          collectButtonSource={collectButtonSource}
          isInModal={isInModal}
        />
      </div>
    </MintComponentProvider>
  );
};
